import { useCallback, useEffect, useState } from "preact/hooks";
import { AddMessageScreen } from "./add-message-screen.tsx";
import { AllowlistEditor } from "./allowlist-editor.tsx";
import {
  categoryTitle,
  configure,
  getLibrary,
  getVoices,
  type Library,
  type VoiceOption,
  type Whoami,
  whoami,
} from "./api.ts";
import { BoothMark } from "./booth-mark.tsx";
import { message } from "./errors.ts";
import { HomeScreen } from "./home-screen.tsx";
import { Gear, Info } from "./icons.tsx";
import { OverflowMenu } from "./menu.tsx";
import { type Navigation, useNavigation, type View } from "./navigation.ts";
import { NumberScreen, type Saved } from "./number-screen.tsx";
import { parseConfig, type SiteConfig } from "./oauth.ts";
import { cacheDelay, cacheEvery } from "./phrases.ts";
import { RecordScreen } from "./record-screen.tsx";
import { ReviewRecordingScreen } from "./review-recording-screen.tsx";
import { completeSignIn, signedIn, signOut } from "./session.ts";
import { SetUpNumberScreen } from "./setup-number-screen.tsx";
import { SignInError, type SignInFailure } from "./sign-in-errors.ts";
import { SignInScreen } from "./sign-in-screen.tsx";
import { TypeMessageScreen } from "./type-message-screen.tsx";
import { Callout, Card } from "./ui.tsx";
import { VoiceCatalog } from "./voice-catalog.tsx";

type Stage =
  | { name: "loading" }
  | { name: "unconfigured"; error: SignInFailure }
  | { name: "signed-out"; config: SiteConfig }
  | { name: "signed-in"; config: SiteConfig; user: Whoami }
  | { name: "failed"; config: SiteConfig; error: SignInFailure };

/**
 * The deploy writes config.json beside the page, so the built bundle carries no
 * pool ids and the same build works for local development.
 */
async function start(): Promise<Stage> {
  let config: SiteConfig;
  try {
    const response = await fetch("/config.json", { cache: "no-store" });
    config = parseConfig(await response.json());
    configure(config);
  } catch (error: unknown) {
    return {
      name: "unconfigured",
      error: { title: "This site is not set up", detail: message(error) },
    };
  }

  try {
    await completeSignIn(config);
  } catch (error: unknown) {
    // The sign-in callback already worked out what to say; anything else here
    // is unexpected and shown in its own words.
    const failure =
      error instanceof SignInError ? error.failure : { title: message(error) };
    return { name: "failed", config, error: failure };
  }

  if (!signedIn()) return { name: "signed-out", config };
  try {
    return { name: "signed-in", config, user: await whoami() };
  } catch (error: unknown) {
    return { name: "failed", config, error: { title: message(error) } };
  }
}

export function App() {
  const [stage, setStage] = useState<Stage>({ name: "loading" });

  useEffect(() => {
    let live = true;
    start().then((next) => {
      if (live) setStage(next);
    });
    return () => {
      live = false;
    };
  }, []);

  if (stage.name === "loading") return <p class="empty">Loading…</p>;
  if (stage.name === "unconfigured") {
    return <SignInScreen config={null} error={stage.error} />;
  }
  if (stage.name === "failed") {
    return <SignInScreen config={stage.config} error={stage.error} />;
  }
  if (stage.name === "signed-out") {
    return <SignInScreen config={stage.config} error={null} />;
  }
  return <Shell config={stage.config} user={stage.user} />;
}

function Shell({ config, user }: { config: SiteConfig; user: Whoami }) {
  const [library, setLibrary] = useState<Library | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saved, setSaved] = useState<Saved | null>(null);
  const [delayNote, setDelayNote] = useState(false);
  const [voices, setVoices] = useState<VoiceOption[]>([]);
  const nav = useNavigation({ name: "home" });

  const reload = useCallback(async () => {
    setLibrary(await getLibrary());
  }, []);

  useEffect(() => {
    reload().catch((reason: unknown) => setLoadError(message(reason)));
  }, [reload]);

  // Read once, only so a message row can name the voice that read it. Names do
  // not change when the catalog is curated, and a row whose voice the account
  // no longer has just says "Typed" — so a failure here is not worth reporting.
  useEffect(() => {
    getVoices().then(
      (list) => setVoices(list.voices),
      () => undefined,
    );
  }, []);

  // Every screen change lands focus on the new screen's title.
  useEffect(() => {
    const title = document.querySelector<HTMLElement>("[data-screen-title]");
    title?.focus();
  }, [nav.view]);

  /** A move forward, which is also what clears the last save's highlight. */
  function go(view: View): void {
    setSaved(null);
    nav.push(view);
  }

  const onHome = nav.view.name === "home";

  return (
    <div class="page">
      <header class="app-head">
        <div class="brand">
          <BoothMark size={28} />
          {onHome ? (
            <h1 class="page-title" tabIndex={-1} data-screen-title>
              Booth Messages
            </h1>
          ) : (
            <h1 class="wordmark">Booth Messages</h1>
          )}
        </div>
        {/* Voices and the allowlist shape the booth, not the person signed in,
            so they do not hang off an identity control. Signing out is the only
            thing here that is about the person, and one item is not a menu. */}
        <OverflowMenu
          label="Settings"
          trigger={<Gear />}
          header={`Signed in as ${user.email}`}
          items={[
            { label: "Voices", onSelect: () => go({ name: "voices" }) },
            ...(user.admin
              ? [
                  {
                    label: "Who can sign in",
                    onSelect: () => go({ name: "allowlist" }),
                  },
                ]
              : []),
            { label: "Sign out", onSelect: () => signOut(config) },
          ]}
        />
      </header>

      {onHome && (
        <p class="lede">
          Changes reach the phone in {cacheDelay()}.{" "}
          <button
            type="button"
            class="icon-btn info-btn"
            aria-label="Why a change takes a moment"
            aria-expanded={delayNote}
            onClick={() => setDelayNote(!delayNote)}
          >
            <Info />
          </button>
        </p>
      )}
      {onHome && delayNote && (
        <Callout tone="info" title="Why changes take a minute">
          The phone reloads its messages every {cacheEvery()}, so a change can take one
          call to show up.
        </Callout>
      )}

      {loadError && <Callout tone="bad" title={loadError} />}
      {!library && !loadError && <p class="empty">Loading…</p>}
      {library && (
        <Screen
          library={library}
          voices={voices}
          nav={nav}
          go={go}
          reload={reload}
          user={user}
          saved={saved}
          setSaved={setSaved}
        />
      )}
    </div>
  );
}

interface ScreenProps {
  library: Library;
  /** Every voice the engine offers, for naming the one a typed message used. */
  voices: VoiceOption[];
  nav: Navigation;
  go: (view: View) => void;
  reload: () => Promise<void>;
  user: Whoami;
  saved: Saved | null;
  setSaved: (saved: Saved | null) => void;
}

function Screen({
  library,
  voices,
  nav,
  go,
  reload,
  user,
  saved,
  setSaved,
}: ScreenProps) {
  const view = nav.view;

  if (view.name === "home") {
    return (
      <HomeScreen
        library={library}
        onOpen={(digit, category) => go({ name: "number", digit, category })}
        onSetUp={(digit) => go({ name: "setup", digit })}
      />
    );
  }

  if (view.name === "setup") {
    return (
      <SetUpNumberScreen
        digit={view.digit}
        library={library}
        onClose={nav.back}
        onCreated={(category) => {
          void reload();
          nav.replace({ name: "number", digit: view.digit, category });
        }}
      />
    );
  }

  if (view.name === "voices") return <VoiceCatalog onBack={nav.back} />;

  if (view.name === "allowlist") {
    if (!user.admin) return <p class="empty">This list is for admins.</p>;
    return <AllowlistEditor me={user.email} onBack={nav.back} />;
  }

  if (view.name === "add" || view.name === "record" || view.name === "review") {
    const back = () => nav.back();
    if (view.name === "add") {
      return (
        <AddMessageScreen
          onBack={back}
          onRecord={() =>
            go({ name: "record", digit: view.digit, category: view.category })
          }
          onType={() =>
            go({ name: "type", digit: view.digit, category: view.category })
          }
        />
      );
    }
    if (view.name === "record") {
      return (
        <RecordScreen
          topic={categoryTitle(library, view.category)}
          onBack={back}
          onRecorded={(recording) => {
            go({
              name: "review",
              digit: view.digit,
              category: view.category,
              recording,
            });
          }}
        />
      );
    }
    return (
      <ReviewRecordingScreen
        category={view.category}
        recording={view.recording}
        onBack={back}
        onAgain={back}
        onSaved={(id) => {
          setSaved({ category: view.category, id });
          void reload();
          nav.backTo("number");
        }}
      />
    );
  }

  if (view.name === "type") {
    return (
      <TypeMessageScreen
        library={library}
        category={view.category}
        editing={view.editing}
        onBack={nav.back}
        onSaved={(id) => {
          setSaved({ category: view.category, id });
          void reload();
          nav.backTo("number");
        }}
      />
    );
  }

  // A topic is its number, so the only way this can miss is another browser
  // setting up the same digit while this screen sat on the stack.
  if (library.digits[view.digit] !== view.category) {
    return (
      <Card>
        <p class="empty">This number has a different topic now.</p>
      </Card>
    );
  }
  return (
    <NumberScreen
      library={library}
      voices={voices}
      digit={view.digit}
      category={view.category}
      saved={saved}
      onDismissSaved={() => setSaved(null)}
      onBack={nav.back}
      onAdd={() => go({ name: "add", digit: view.digit, category: view.category })}
      onEdit={(entry) => {
        go({
          name: "type",
          digit: view.digit,
          category: view.category,
          editing: entry,
        });
      }}
      reload={reload}
    />
  );
}
